/**
 * Sessions Store - Course Session State Management
 *
 * Handles:
 * - Loading sessions for a course
 * - Creating new sessions (Termine)
 * - Caching in IndexedDB
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { sessionsApi } from '@/api/sessions.api'
import { db } from '@/db'
import { isNetworkError } from '@/api/client'
import type {
  CourseSessionDto,
  CreateCourseSessionDto,
  UpdateCourseSessionDto,
} from '@/types/api.types'
import type { LocalSessionInstructorUpdate } from '@/types/local.types'

export const useSessionsStore = defineStore('sessions', () => {
  // ==========================================================================
  // State
  // ==========================================================================

  const sessions = ref<Map<number, CourseSessionDto[]>>(new Map())
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastFetched = ref<Map<number, number>>(new Map())

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000

  // ==========================================================================
  // Getters
  // ==========================================================================

  /**
   * Get sessions for a specific course
   */
  const getByCourseId = computed(() => {
    return (courseId: number): CourseSessionDto[] => {
      return sessions.value.get(courseId) || []
    }
  })

  /**
   * Get today's sessions for a course
   */
  const getTodayByCourseId = computed(() => {
    return (courseId: number): CourseSessionDto[] => {
      const courseSessions = sessions.value.get(courseId) || []
      const today = new Date()
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)

      return courseSessions
        .filter((s) => {
          const sessionDate = new Date(s.date)
          return sessionDate >= todayStart && sessionDate < todayEnd
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    }
  })

  /**
   * Get past sessions for a course (before today)
   */
  const getPastByCourseId = computed(() => {
    return (courseId: number): CourseSessionDto[] => {
      const courseSessions = sessions.value.get(courseId) || []
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      return courseSessions
        .filter((s) => new Date(s.date) < today)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    }
  })

  /**
   * Get upcoming sessions for a course (today or future) - used for stats only
   */
  const getUpcomingByCourseId = computed(() => {
    return (courseId: number): CourseSessionDto[] => {
      const courseSessions = sessions.value.get(courseId) || []
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      return courseSessions
        .filter((s) => new Date(s.date) >= today)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    }
  })

  /**
   * Get next upcoming session for a course
   */
  const getNextSession = computed(() => {
    return (courseId: number): CourseSessionDto | undefined => {
      const upcoming = getUpcomingByCourseId.value(courseId)
      return upcoming[0]
    }
  })

  /**
   * Get the most relevant session for a course's Quick-Action button on the Dashboard.
   *
   * Used by DashboardView → CourseCard to determine which session the
   * "Anwesenheit erfassen" button navigates to, and which date label
   * is shown above the button (e.g. "Heute", "Fr. 13.02.").
   *
   * Priority:
   * 1. **Today's session** → label shows "Heute"
   * 2. **Next upcoming session** (earliest future date) → label shows e.g. "Fr. 20.02."
   * 3. **Most recent past session** (≤ 14 days ago AND not yet billed) → label shows e.g. "Fr. 13.02."
   *
   * Returns `undefined` if no relevant session exists — in that case the
   * CourseCard shows no date label and the button navigates to CourseDetail instead.
   */
  const getMostRelevantSession = computed(() => {
    return (courseId: number): CourseSessionDto | undefined => {
      // First check for today's session
      const todaySessions = getTodayByCourseId.value(courseId)
      if (todaySessions.length > 0) {
        return todaySessions[0]
      }

      // Then check for upcoming sessions
      const upcoming = getUpcomingByCourseId.value(courseId)
      if (upcoming.length > 0) {
        return upcoming[0]
      }

      // Finally, get most recent past session (within 2 weeks, not billed)
      const pastSessions = getPastByCourseId.value(courseId)
      const twoWeeksAgo = new Date()
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14)

      const recentPast = pastSessions
        .filter((s) => {
          const sessionDate = new Date(s.date)
          return sessionDate >= twoWeeksAgo && !s.isBilled
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

      return recentPast[0]
    }
  })

  /**
   * Get session by ID
   */
  const getById = computed(() => {
    return (sessionId: number): CourseSessionDto | undefined => {
      for (const courseSessions of sessions.value.values()) {
        const session = courseSessions.find((s) => s.id === sessionId)
        if (session) return session
      }
      return undefined
    }
  })

  // ==========================================================================
  // Actions
  // ==========================================================================

  /**
   * Fetch sessions for a course
   */
  async function fetchByCourse(courseId: number, forceRefresh = false): Promise<void> {
    // Check cache validity
    const lastFetch = lastFetched.value.get(courseId) || 0
    const isCacheValid = Date.now() - lastFetch < CACHE_DURATION

    if (isCacheValid && !forceRefresh && sessions.value.has(courseId)) {
      return
    }

    // Offline + data already in memory → skip API & IndexedDB roundtrip
    if (!navigator.onLine && sessions.value.has(courseId)) return

    isLoading.value = true
    error.value = null

    try {
      // Try API first
      const data = await sessionsApi.getByCourse(courseId)

      // Update state
      sessions.value.set(courseId, data)
      lastFetched.value.set(courseId, Date.now())

      // Cache in IndexedDB
      await cacheSessionsLocally(courseId, data)
    } catch (e) {
      // Try to load from IndexedDB on network error
      if (isNetworkError(e)) {
        const cached = await loadFromCache(courseId)
        if (cached.length > 0) {
          sessions.value.set(courseId, cached)
          error.value = null
          return
        }
      }

      error.value = e instanceof Error ? e.message : 'Fehler beim Laden der Termine'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update instructor assignment of a session (leadInstructorId / coInstructorId)
   */
  async function updateSession(id: number, data: UpdateCourseSessionDto): Promise<void> {
    await sessionsApi.update(id, data)

    // Lokalen Store-Cache aktualisieren
    for (const [courseId, courseSessions] of sessions.value) {
      const idx = courseSessions.findIndex((s) => s.id === id)
      if (idx !== -1) {
        const updated: CourseSessionDto = { ...courseSessions[idx]!, ...data }
        sessions.value.set(courseId, [
          ...courseSessions.slice(0, idx),
          updated,
          ...courseSessions.slice(idx + 1),
        ])
        // IndexedDB-Cache ebenfalls aktualisieren
        await db.sessions.put(updated)
        break
      }
    }
  }

  /**
   * Update session instructor assignments with offline support.
   *
   * Online:  Sofort via API (PUT /CourseSessions/{id}).
   * Offline: Optimistisch lokal aktualisieren + in SyncQueue queuen.
   */
  async function updateSessionInstructors(
    sessionId: number,
    leadInstructorId: number | null | undefined,
    coInstructorId: number | null | undefined
  ): Promise<void> {
    // Das Datum MUSS mitgesendet werden: `UpdateCourseSessionDto.Date` ist im Backend ein
    // nicht-nullable DateTime. Fehlt das Feld im JSON, mappt das Backend DateTime.MinValue
    // (0001-01-01) auf die Entity — der Termin verliert sein Datum und fällt damit aus jeder
    // Abrechnungsperiode heraus (die Abrechnung filtert auf Date >= periodStart && <= periodEnd).
    // Genau so sind die undatierten Termine im Bestand entstanden.
    const currentDate = getById.value(sessionId)?.date
    if (!currentDate) {
      throw new Error(
        `Termin ${sessionId} ist nicht im Store-Cache — ohne bekanntes Datum wird nicht gespeichert, ` +
          `sonst würde das Backend das Datum auf 0001-01-01 zurücksetzen.`
      )
    }

    if (navigator.onLine) {
      const update: UpdateCourseSessionDto = { date: currentDate }
      if (leadInstructorId !== undefined && leadInstructorId !== null) {
        update.leadInstructorId = leadInstructorId
      }
      if (coInstructorId !== undefined && coInstructorId !== null) {
        update.coInstructorId = coInstructorId
      }
      await sessionsApi.update(sessionId, update)
    } else {
      // Offline: in SyncQueue einreihen — inkl. Datum, damit der Sync es mitsenden kann
      const payload: LocalSessionInstructorUpdate = { sessionId, date: currentDate }
      if (leadInstructorId !== undefined) payload.leadInstructorId = leadInstructorId
      if (coInstructorId !== undefined) payload.coInstructorId = coInstructorId
      await db.syncQueue.add({
        type: 'session-instructor',
        action: 'update',
        localId: uuidv4(),
        data: payload,
        createdAt: new Date().toISOString(),
        retryCount: 0,
      })
    }

    // Optimistisch: Session im lokalen Store-Cache aktualisieren
    for (const [courseId, courseSessions] of sessions.value) {
      const idx = courseSessions.findIndex((s) => s.id === sessionId)
      if (idx !== -1) {
        const current = courseSessions[idx]!
        const updated = { ...current }
        if (leadInstructorId !== undefined) {
          if (leadInstructorId !== null) {
            updated.leadInstructorId = leadInstructorId
          } else {
            delete updated.leadInstructorId
          }
        }
        if (coInstructorId !== undefined) {
          if (coInstructorId !== null) {
            updated.coInstructorId = coInstructorId
          } else {
            delete updated.coInstructorId
          }
        }
        sessions.value.set(courseId, [
          ...courseSessions.slice(0, idx),
          updated,
          ...courseSessions.slice(idx + 1),
        ])
        await db.sessions.put(updated)
        break
      }
    }
  }

  /**
   * Sync pending session-instructor changes from SyncQueue.
   * Returns { success, failed } counts.
   */
  async function syncPendingInstructors(): Promise<{ success: number; failed: number }> {
    const items = await db.syncQueue.where('type').equals('session-instructor').toArray()
    if (items.length === 0) return { success: 0, failed: 0 }

    let success = 0
    let failed = 0

    for (const item of items) {
      const payload = item.data as LocalSessionInstructorUpdate
      try {
        // Datum aus dem Queue-Eintrag, sonst aus dem Cache — ohne Datum NICHT senden,
        // weil das Backend den Termin sonst auf 0001-01-01 zurücksetzt (siehe
        // updateSessionInstructors).
        const queuedDate = payload.date ?? getById.value(payload.sessionId)?.date
        if (!queuedDate) {
          throw new Error(
            `Termin ${payload.sessionId}: kein Datum im Queue-Eintrag und keins im Cache — ` +
              `Sync übersprungen, um das Datum nicht zu zerstören.`
          )
        }

        const update: UpdateCourseSessionDto = { date: queuedDate }
        if (payload.leadInstructorId !== undefined && payload.leadInstructorId !== null) {
          update.leadInstructorId = payload.leadInstructorId
        }
        if (payload.coInstructorId !== undefined && payload.coInstructorId !== null) {
          update.coInstructorId = payload.coInstructorId
        }
        await sessionsApi.update(payload.sessionId, update)
        await db.syncQueue.delete(item.id!)
        success++
      } catch (e) {
        await db.syncQueue.update(item.id!, {
          retryCount: item.retryCount + 1,
          lastError: e instanceof Error ? e.message : 'Unbekannter Fehler',
        })
        failed++
      }
    }

    return { success, failed }
  }

  /**
   * Create a new session
   */
  async function createSession(data: CreateCourseSessionDto): Promise<CourseSessionDto> {
    isLoading.value = true
    error.value = null

    try {
      const newSession = await sessionsApi.create(data)

      // Update local state
      const courseSessions = sessions.value.get(data.courseId) || []
      sessions.value.set(data.courseId, [...courseSessions, newSession])

      // Update cache
      await db.sessions.put(newSession)

      return newSession
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Fehler beim Erstellen des Termins'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Cache sessions in IndexedDB
   */
  async function cacheSessionsLocally(courseId: number, data: CourseSessionDto[]): Promise<void> {
    try {
      await db.transaction('rw', db.sessions, async () => {
        // Remove old sessions for this course
        await db.sessions.where('courseId').equals(courseId).delete()
        // Add new sessions
        await db.sessions.bulkPut(data)
      })
    } catch (e) {
      console.warn('[SessionsStore] Failed to cache sessions:', e)
    }
  }

  /**
   * Load sessions from IndexedDB cache
   */
  async function loadFromCache(courseId: number): Promise<CourseSessionDto[]> {
    try {
      return await db.sessions.where('courseId').equals(courseId).toArray()
    } catch (e) {
      console.warn('[SessionsStore] Failed to load from cache:', e)
      return []
    }
  }

  /**
   * Clear sessions for a course
   */
  function clearCourse(courseId: number): void {
    sessions.value.delete(courseId)
    lastFetched.value.delete(courseId)
  }

  /**
   * Invalidate all cache timestamps so next fetchByCourse hits the API.
   * Does NOT clear in-memory data (avoids flicker), just forces re-fetch.
   */
  function invalidateCache(): void {
    lastFetched.value.clear()
  }

  /**
   * Reset store
   */
  function $reset(): void {
    sessions.value.clear()
    lastFetched.value.clear()
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    sessions,
    isLoading,
    error,
    lastFetched,

    // Getters
    getByCourseId,
    getTodayByCourseId,
    getPastByCourseId,
    getUpcomingByCourseId,
    getNextSession,
    getMostRelevantSession,
    getById,

    // Actions
    fetchByCourse,
    updateSession,
    updateSessionInstructors,
    syncPendingInstructors,
    createSession,
    clearCourse,
    invalidateCache,
    $reset,
  }
})
